import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
	Box,
	Button,
	Input,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Tag,
	Text,
	VStack,
	Wrap,
	WrapItem,
} from '@bonusly/design-system';

export interface BizyFeedbackCategory {
	/** Value submitted to the backend; must match the server-side enum. */
	id: string;
	/** i18n key (admin_bizy namespace) for the chip label. */
	labelKey: string;
}

interface BizyFeedbackModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSubmit: (category: string, details?: string) => void;
	/** Override the category chips. Defaults to the chat-response categories. */
	categories?: readonly BizyFeedbackCategory[];
	/** Override the privacy-notice i18n key. Defaults to the chat-conversation notice. */
	privacyNoticeKey?: string;
}

/** Default categories for feedback on a chat response (thumbs-down on a reply). */
export const CHAT_FEEDBACK_CATEGORIES: readonly BizyFeedbackCategory[] = [
	{ id: 'incorrect', labelKey: 'feedback.categories.incorrect' },
	{ id: 'not_asked', labelKey: 'feedback.categories.notAsked' },
	{ id: 'slow', labelKey: 'feedback.categories.slow' },
	{ id: 'tone', labelKey: 'feedback.categories.tone' },
	{ id: 'safety', labelKey: 'feedback.categories.safety' },
	{ id: 'other', labelKey: 'feedback.categories.other' },
];

/**
 * Modal for collecting detailed negative feedback on Bizy.
 * Follows the ChatGPT feedback pattern with category chips and optional details.
 * Categories and the privacy notice are overridable so the same modal serves
 * both chat responses and observed-moment ("Noticed") cards.
 */
export const BizyFeedbackModal: FC<BizyFeedbackModalProps> = ({
	isOpen,
	onClose,
	onSubmit,
	categories = CHAT_FEEDBACK_CATEGORIES,
	privacyNoticeKey = 'feedback.privacyNotice',
}) => {
	const { t } = useTranslation('admin_bizy');
	const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
	const [details, setDetails] = useState('');

	const handleSubmit = () => {
		if (selectedCategory) {
			onSubmit(selectedCategory, details.trim() || undefined);
			// Reset state after submission
			setSelectedCategory(null);
			setDetails('');
		}
	};

	const handleClose = () => {
		// Reset state on close
		setSelectedCategory(null);
		setDetails('');
		onClose();
	};

	return (
		<Modal isOpen={isOpen} onClose={handleClose} size="md" isCentered>
			<ModalOverlay bg="bg.overlayBackdrop" backdropFilter="blur(4px)" />
			<ModalContent borderRadius="xl" bg="bg.surface" mx={4}>
				<ModalHeader pb={2}>
					<Text fontSize="lg" fontWeight="semibold">
						{t('feedback.modalTitle')}
					</Text>
				</ModalHeader>
				<ModalCloseButton borderRadius="full" />

				<ModalBody py={4}>
					<VStack spacing={4} align="stretch">
						{/* Category chips */}
						<Wrap spacing={2}>
							{categories.map((category) => {
								const isSelected = selectedCategory === category.id;
								return (
									<WrapItem key={category.id}>
										<Tag
											size="md"
											variant={isSelected ? 'solid' : 'outline'}
											colorScheme={isSelected ? 'gray' : undefined}
											cursor="pointer"
											borderRadius="full"
											fontWeight="medium"
											px={3}
											py={1.5}
											bg={isSelected ? 'secondary.bg' : 'bg.surface'}
											color={isSelected ? 'secondary.fgOnSurface' : 'text.default'}
											borderColor="border.default"
											borderWidth="1px"
											onClick={() => setSelectedCategory(category.id)}
											_hover={{
												bg: isSelected ? 'secondary.bgHover' : 'secondary.bgAlpha',
											}}
										>
											{t(category.labelKey)}
										</Tag>
									</WrapItem>
								);
							})}
						</Wrap>

						{/* Optional details input */}
						<Box>
							<Input placeholder={t('feedback.detailsPlaceholder')} value={details} onChange={(e) => setDetails(e.target.value)} />
						</Box>

						{/* Privacy notice */}
						<Text fontSize="sm" color="text.soft">
							{t(privacyNoticeKey)}
						</Text>
					</VStack>
				</ModalBody>

				<ModalFooter borderTop="1px solid" borderColor="border.soft" pt={4}>
					<Button onClick={handleSubmit} isDisabled={!selectedCategory}>
						{t('feedback.submit')}
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};
